import React, { useState, createContext, useContext, useEffect } from "react";
import Swal from "sweetalert2";
import api from "../../Utils/api";
import { timesheetStatus } from "../../Utils/constants";

const apiPath = {
  fetchTimesheets: "/timesheet/gettimesheets",
  fetchTimesheetDetails: "/timesheet/gettimesheetdetails",
  approveTimesheet: "/timesheet/approvetimesheet",
  rejectTimesheet: "/timesheet/rejecttimesheet",
  fetchTimesheetsForJob: "/timesheet/gettimesheetsforjob",
  fetchTimesheetsByDate: "/timesheet/gettimesheetbydate",
  getTimesheetIdFromLink: "/timesheet/link/gettimesheetid",
};

const nullFn = () => null;
const TimesheetContext = createContext({
  loading: false,
  timesheets: [],
  fetchTimesheets: nullFn,
  approveTimesheet: nullFn,
  rejectTimesheet: nullFn,
  selectedTimesheet: null,
  setSelectedTimesheet: nullFn,
  timesheetDetails: {},
  fetchTimesheetsForJob: nullFn,
  fetchTimesheetsByDate: nullFn,
  getTimesheetIdFromLink: nullFn,
});

export const useTimesheetProvider = () => useContext(TimesheetContext);
const TimesheetProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState([]);
  const [timesheetDetails, setTimesheetDetails] = useState({});
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);

  useEffect(() => {
    if (selectedTimesheet) fetchTimesheetDetails(selectedTimesheet);
  }, [selectedTimesheet]);

  const fetchTimesheets = async () => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheets, {
        params: { userId: localStorage.getItem("userId"), userType: "CLIENT" },
      });

      if (res.status === 200 && res.data?.success) {
        const { result, mappings } = res.data;
        const jobs = mappings.reduce(
          (obj, item) => (
            (obj[item.job_id] = {
              consultant: item.consultant,
              vendor: item.vendor,
            }),
            obj
          ),
          {}
        );
        setTimesheets(
          result.map((item) => ({ ...item, ...jobs[item.job_id] }))
        );
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimesheetDetails = async (timesheet_id) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheetDetails, {
        params: { timesheet_id },
      });

      if (res.status === 200 && res.data?.success) {
        setTimesheetDetails({
          ...timesheetDetails,
          [timesheet_id]: res.data.result,
        });
      } else {
        throw new Error("Error occurred while fetching timesheet details");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const approveTimesheet = async (timesheet_id, comments) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.approveTimesheet, {
        timesheet_id,
        comments,
        userId: localStorage.getItem("userId"),
      });

      if (res.status === 200 && res.data?.success) {
        const temp = [...timesheets];
        const index = temp.findIndex((item) => item.id === timesheet_id);
        if (index > -1) {
          temp[index].status = timesheetStatus.approved;
          setTimesheets(temp);
        }

        setLoading(false);
        Swal.fire({
          icon: "success",
          title: "Timesheet approved successfully",
          timer: 1500,
        });

        return true;
      } else {
        throw new Error("Error occurred while approving timesheet");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });

      return false;
    }
  };

  const rejectTimesheet = async (timesheet_id, comments) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.rejectTimesheet, {
        timesheet_id,
        comments,
        userId: localStorage.getItem("userId"),
      });

      if (res.status === 200 && res.data?.success) {
        const temp = [...timesheets];
        const index = temp.findIndex((item) => item.id === timesheet_id);
        if (index > -1) {
          temp[index].status = timesheetStatus.rejected;
          setTimesheets(temp);
        }

        setLoading(false);
        Swal.fire({
          icon: "success",
          title: "Timesheet rejected successfully",
          timer: 1500,
        });

        return true;
      } else {
        throw new Error("Error occurred while rejecting timesheet");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong!",
      });

      return false;
    }
  };

  const fetchTimesheetsForJob = async (job_id) => {
    setLoading(true);
    try {
      const res = await api.get(apiPath.fetchTimesheetsForJob, {
        params: { job_id },
      });

      if (res.status === 200 && res.data?.success) {
        setLoading(false);
        return res.data.result;
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      return [];
    }
  };

  const fetchTimesheetsByDate = async ({ jobId, startDate }) => {
    setLoading(true);
    try {
      const res = await api.post(apiPath.fetchTimesheetsByDate, {
        jobId,
        startDate,
        userId: localStorage.getItem("userId"),
        userType: "CLIENT",
      });

      if (res.status === 200 && res.data?.success) {
        if (res.data.msg === "No timesheet found") {
          setSelectedTimesheet(null);
        } else {
          setSelectedTimesheet(res.data.timesheet.id);
          setTimesheetDetails({
            ...timesheetDetails,
            [res.data.timesheet.id]: res.data.result,
          });
        }
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const getTimesheetIdFromLink = async (id) => {
    try {
      const res = await api.get(apiPath.getTimesheetIdFromLink, {
        params: { id },
      });

      if (res.status === 200 && res.data?.success) {
        if (res.data.result?.length > 0) setSelectedTimesheet(res.data.result);
      } else {
        throw new Error("Error occurred while fetching timesheets");
      }
    } catch (e) {
      console.log(e);
    }
  };

  const value = {
    loading,
    timesheets,
    fetchTimesheets,
    approveTimesheet,
    rejectTimesheet,
    selectedTimesheet,
    setSelectedTimesheet,
    timesheetDetails,
    fetchTimesheetsForJob,
    fetchTimesheetsByDate,
    getTimesheetIdFromLink,
  };

  return (
    <TimesheetContext.Provider value={value}>
      {children}
    </TimesheetContext.Provider>
  );
};

export default TimesheetProvider;
